/** Access page (a company's administrators) and Tenants pages (EVT platform administrators). */

export const access = {
  title: 'Access',
  subtitle: (tenant: string) => `Who can use the portal for ${tenant}, and how they sign in.`,
  stepUp:
    'You signed in with an e-mail code. Managing access needs a passkey: sign out and sign in again — right after the code, choose "Create a passkey" (or use the one you have).',

  policyTitle: 'Sign-in policy',
  policyPasskey: 'Passkey (or SSO)',
  policyEmailCode: 'Passkey, SSO or an e-mailed code',
  policyDomainsAny: 'Invitations to any e-mail domain',
  policyDomains: (domains: string) => `Invitations only to ${domains}`,
  policyManagedByEvt: 'Set by EVT. Ask your EVT contact to change it.',

  inviteTitle: 'Invite people',
  inviteHelp:
    'One e-mail address per line (or separated by commas). Each person gets an invitation e-mail with the steps to sign in.',
  inviteEmails: 'E-mail addresses',
  inviteEmailsPlaceholder: 'ana@company.com\nbruno@company.com',
  inviteRole: 'Role',
  inviteSendEmail: 'Send the invitation e-mail',
  inviteLanguage: 'E-mail language',
  inviteSubmit: 'Invite',
  inviteOutcomeInvited: 'Invited',
  inviteOutcomeReactivated: 'Access restored',
  inviteOutcomeAlready: 'Already has access',
  inviteEmailFailed: 'invitation e-mail failed — use "Resend invitation"',
  inviteEmailed: 'e-mail sent',
  inviteOutside: (emails: string[], domains: string) =>
    emails.length === 1
      ? `Nobody was invited: ${emails[0]} is outside ${domains}. Fix or remove it and invite again.`
      : `Nobody was invited: ${emails.join(', ')} are outside ${domains}. Fix or remove them and invite again.`,

  roleMember: 'Member',
  roleAdmin: 'Administrator',
  roleHelp: 'Administrators also manage who has access.',

  membersTitle: 'People',
  membersEmpty: 'Nobody has access yet. Invite the first people above.',
  searchPlaceholder: 'Search by e-mail',
  showRevoked: 'Show removed people',
  colPerson: 'Person',
  colRole: 'Role',
  colStatus: 'Status',
  colSignIn: 'Sign-in',
  colActions: '',
  statusActive: 'Active',
  statusInvited: 'Invited',
  statusRevoked: 'Removed',
  neverSignedIn: 'Never signed in',
  lastSignIn: (ago: string) => `Last sign-in ${ago}`,
  passkeys: (n: number) => (n === 1 ? '1 passkey' : `${n} passkeys`),
  sso: 'SSO',
  you: 'you',
  viaDomain: 'via e-mail domain',
  viaSso: 'via company SSO',
  otherTenants: (n: number) => (n === 1 ? 'also in 1 other company' : `also in ${n} other companies`),

  actionMakeAdmin: 'Make administrator',
  actionMakeMember: 'Make member',
  actionRevoke: 'Remove access',
  actionReactivate: 'Restore access',
  actionResend: 'Resend invitation',
  actionSignOut: 'Sign out everywhere',
  actionResetPasskeys: 'Reset passkeys',
  confirm: 'Confirm',
  confirmRevoke: (email: string) => `Remove ${email}'s access? They are signed out right away.`,
  confirmResetPasskeys: (email: string) =>
    `Delete all of ${email}'s passkeys? They sign in again with an e-mail code and create a new one.`,
  confirmSignOut: (email: string) => `Sign ${email} out on every device?`,
  doneResent: 'Invitation sent again.',
  doneSignedOut: 'Signed out everywhere.',
  donePasskeysReset: (n: number) => (n === 1 ? '1 passkey deleted.' : `${n} passkeys deleted.`),

  activityTitle: 'Recent changes',
  activityEmpty: 'No changes yet.',
  activityBy: (actor: string, platform: boolean) => (platform ? `${actor} (EVT)` : actor),
  actions: {
    'tenant.created': 'created the company',
    'tenant.updated': 'changed the company settings',
    'tenant.flows_changed': "changed the company's flows",
    'member.invited': 'invited',
    'member.reinvited': 'sent a new invitation to',
    'member.role_changed': 'changed the role of',
    'member.revoked': 'removed the access of',
    'member.reactivated': 'restored the access of',
    'member.signed_out': 'signed out everywhere:',
    'member.passkeys_reset': 'reset the passkeys of'
  } as Record<string, string>,
  activityFlowsAdded: (names: string) => `added ${names}`,
  activityFlowsRemoved: (names: string) => `removed ${names}`,
  activityFlowsModeAll: 'now every offered flow',
  activityFlowsModeSelected: 'now only the chosen flows',

  // ── Platform (EVT) ──
  tenantsTitle: 'Tenants',
  tenantsSubtitle:
    'Every company on the portal. Create a company, appoint its first administrator and decide how its people sign in.',
  createTitle: 'New company',
  createHelp:
    'The id must be exactly the contractor id used in fr-docs (flow rows, signing secret) and mail-reader — the same string everywhere.',
  fieldId: 'Contractor id',
  fieldIdPlaceholder: 'heineken-br',
  fieldName: 'Display name',
  fieldNamePlaceholder: 'Heineken Brazil',
  fieldFirstAdmin: 'First administrator (e-mail)',
  fieldEmailCode: 'Allow sign-in with an e-mailed code',
  fieldEmailCodeHelp:
    'For companies whose managed computers cannot hold a passkey. A code proves only control of the mailbox; managing access still requires a passkey or SSO.',
  fieldDomains: 'Allowed e-mail domains for invitations',
  fieldDomainsHelp: 'Comma-separated, e.g. heineken.com.br. Empty = any domain.',
  createFlowsHelp:
    'A new company sees no flow until you choose its flows (one that already has people keeps theirs until you choose). Its page opens once it is created: choose them there.',
  createSubmit: 'Create company',
  created: (name: string) => `${name} created.`,
  colTenant: 'Company',
  colMembers: 'People',
  colPolicy: 'Sign-in',
  colFlows: 'Flows',
  flowsAllShort: 'Every offered flow',
  flowsAllShortUnchosen: 'Every offered flow · not chosen yet',
  flowsChosenShort: (n: number) => (n === 0 ? 'None chosen' : n === 1 ? '1 flow' : `${n} flows`),
  countsLine: (active: number, admins: number, invited: number) =>
    `${active} ${active === 1 ? 'person' : 'people'} · ${admins} admin${admins === 1 ? '' : 's'}${invited ? ` · ${invited} yet to sign in` : ''}`,
  notRegistered: 'not configured yet',
  statusSuspended: 'Suspended',
  manage: 'Manage',
  tenantsEmpty: 'No companies yet.',

  settingsTitle: 'Company settings',
  saveSettings: 'Save settings',
  saved: 'Settings saved.',
  suspend: 'Suspend company',
  activate: 'Reactivate company',
  confirmSuspend: (name: string) =>
    `Suspend ${name}? Everyone is signed out and nobody can sign in until you reactivate it.`,

  readinessTitle: 'Readiness',
  readinessHelp: 'What this company still needs before its people can run flows end to end.',
  checks: {
    registered: 'Company configured in the portal',
    active: 'Company active',
    signing_secret: 'The portal holds the company signing secret (its fr-docs contractor secret)',
    signing_accepted: "fr-docs accepts the portal's signature for this company",
    flows_chosen: "The company's flows were chosen by EVT",
    flows_visible: 'At least one flow is visible to the company',
    has_admin: 'The company has an administrator'
  } as Record<string, string>,
  checkFix: {
    registered: 'Save the settings above: that registers the company.',
    active: 'Reactivate the company above.',
    signing_secret:
      'Register the contractor secret in smith-factory-web → Contractor secrets, then give the portal the same value (its CONTRACTOR_WEBHOOK_SECRETS_JSON, or Key Vault when the portal reads it) and restart the portal. mail-reader-web needs the same value for e-mailed requests.',
    signing_accepted:
      'The portal and fr-docs hold different secrets for this id (or fr-docs holds none). Put the same value on both sides.',
    flows_chosen:
      'Nobody chose yet, so it sees every offered flow — including flows published later. Choose under Flows above (keeping every flow is a choice too: save it).',
    flows_visible:
      'Choose at least one flow under Flows above. When none is offered, publish one for this company first: smith-factory-web → the flow → Customer portal → visible.',
    has_admin: 'Invite an administrator below.'
  } as Record<string, string>,
  signingUnreachable: (detail: string) => `Could not ask fr-docs: ${detail}`,
  recheck: 'Check again',
  flowsVisible: (names: string) => `Visible flows: ${names}`,
  flowsUnknown: 'Could not ask fr-docs which flows are visible.',
  backToTenants: '← All companies',

  // ── Which flows a company has (EVT) ──
  flowsTitle: 'Flows',
  flowsHelp:
    "Which flows this company's people see, run and keep memories with. Only flows fr-docs offers this company are listed: ones it can run and that are published to the customer portal (smith-factory-web → the flow → Customer portal).",
  flowsModeSelected: 'Only the flows chosen below',
  flowsModeSelectedHelp: 'A flow published later stays out until you choose it.',
  flowsModeAll: 'Every flow offered to this company',
  flowsModeAllHelp:
    "Including flows published later. Meant for EVT's own company; companies created before this choice existed are here until you choose.",
  flowsNoneOffered:
    'fr-docs offers no flow to this company yet. Publish one for it first: smith-factory-web → the flow → Customer portal → visible.',
  flowsOfferedError: (detail: string) =>
    `Could not ask fr-docs which flows it offers this company (${detail}). Only the chosen flows are shown: you can remove them, not add any.`,
  flowsNotOffered: 'No longer offered to this company by fr-docs — hidden whatever you choose. Remove it.',
  flowsNotPublished: 'Not published to the portal: listed only because this environment shows every flow.',
  flowsSummary: (n: number) =>
    n === 0 ? 'Nothing chosen: its people see no flow.' : n === 1 ? '1 flow chosen.' : `${n} flows chosen.`,
  flowsSummaryAll: (n: number) =>
    n === 1 ? 'Its people see the 1 flow offered today.' : `Its people see the ${n} flows offered today.`,
  flowsSave: 'Save flows',
  flowsSaved: "Flows saved. In force on each person's next request — nobody is signed out.",
  flowsReload: 'Reload',
  flowsUnsaved: 'Unsaved changes.',
  flowsNotChosen:
    "Nobody has chosen this company's flows yet: its people see every flow offered to it, including flows published later. Choose below and save — or save as it is to keep every flow."
}
